import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { LineChart, BarChart, PieChart } from '@mui/x-charts';

const CHART_TYPES = ['line', 'bar', 'area', 'pie'];

const METRICS = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'read', label: 'Read' },
];

const COLORS = {
  sent: '#1976d2',
  delivered: '#00a884',
  read: '#9c27b0',
};

const ALIASES = {
  sent: ['sent', 'messages_sent', 'message_sent'],
  delivered: ['delivered', 'messages_delivered', 'message_delivered'],
  read: ['read', 'messages_read', 'message_read'],
};

const getMetricKey = (series) => {
  const name = String(
    series?.key ||
      series?.id ||
      series?.name ||
      series?.label ||
      ''
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  return Object.keys(ALIASES).find((key) =>
    ALIASES[key].includes(name)
  );
};

const ValueMark = ({ x, y, color, dataIndex }) => {
  const values = ValueMark.values?.[color] || [];
  const value = values[dataIndex];

  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r={5}
        fill={color}
      />

      {value !== undefined && (
        <text
          x={x}
          y={y - 12}
          textAnchor="middle"
          fontSize={12}
          fontWeight={700}
          fill="#172033"
        >
          {value}
        </text>
      )}
    </g>
  );
};

export default function ReportingTrendChart({
  graphData,
  height = 320,
  width = 600,
}) {
  const [chartType, setChartType] = useState('line');

  const [selectedMetrics, setSelectedMetrics] = useState([
    'sent',
    'delivered',
    'read',
  ]);

  const series = useMemo(
    () =>
      (graphData?.series ?? [])
        .map((item) => {
          const metricKey = getMetricKey(item);

          if (!metricKey) {
            return null;
          }

          return {
            ...item,
            metricKey,
            label:
              METRICS.find(
                (metric) => metric.key === metricKey
              )?.label || metricKey,
            data: (item.data ?? []).map(
              (value) => Number(value) || 0
            ),
          };
        })
        .filter(Boolean),
    [graphData]
  );

  const availableMetrics = METRICS.filter((metric) =>
    series.some(
      (item) => item.metricKey === metric.key
    )
  );

  const selectedSeries = series.filter((item) =>
    selectedMetrics.includes(item.metricKey)
  );

  const labels =
    graphData?.xAxis?.[0]?.data ||
    graphData?.labels ||
    selectedSeries[0]?.data.map(
      (_, index) => `${index + 1}`
    ) ||
    [];

  const isSingleDate = labels.length === 1;

  const toggleMetric = (key) => {
    setSelectedMetrics((current) => {
      if (current.includes(key)) {
        if (current.length === 1) {
          return current;
        }

        return current.filter(
          (item) => item !== key
        );
      }

      return [...current, key];
    });
  };

  if (
    !series.some((item) =>
      item.data.some((value) => value > 0)
    )
  ) {
    return null;
  }

  const chartLabels =
    chartType === 'area' && isSingleDate
      ? [...labels, '']
      : labels;

  const chartSeries = selectedSeries.map((item) => {
    const data =
      chartType === 'area' && isSingleDate
        ? [...item.data, 0]
        : item.data;

    return {
      id: item.metricKey,
      label: item.label,
      data,
      color: COLORS[item.metricKey],
      showMark: true,
      area: chartType === 'area',
    };
  });

  ValueMark.values = Object.fromEntries(
    chartSeries.map((item) => [
      item.color,
      item.data,
    ])
  );

  const pieData = selectedSeries.map(
    (item, index) => ({
      id: index,
      label: item.label,
      value: item.data.reduce(
        (sum, value) => sum + value,
        0
      ),
      color: COLORS[item.metricKey],
    })
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        sx={{
          mb: 2,
          justifyContent: 'space-between',
          alignItems: {
            md: 'flex-end',
          },
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Chart Type
          </Typography>

          <ButtonGroup size="small">
            {CHART_TYPES.map((type) => (
              <Button
                key={type}
                variant={
                  chartType === type
                    ? 'contained'
                    : 'outlined'
                }
                onClick={() =>
                  setChartType(type)
                }
              >
                {type.charAt(0).toUpperCase() +
                  type.slice(1)}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Metrics
          </Typography>

          <Stack
            direction="row"
            spacing={1}
          >
            {availableMetrics.map((metric) => {
              const selected =
                selectedMetrics.includes(
                  metric.key
                );

              return (
                <Chip
                  key={metric.key}
                  label={metric.label}
                  clickable
                  variant={
                    selected
                      ? 'filled'
                      : 'outlined'
                  }
                  onClick={() =>
                    toggleMetric(metric.key)
                  }
                  sx={{
                    fontWeight: 600,
                    borderColor:
                      COLORS[metric.key],
                    color: selected
                      ? '#fff'
                      : COLORS[metric.key],
                    backgroundColor: selected
                      ? COLORS[metric.key]
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: selected
                        ? COLORS[metric.key]
                        : `${COLORS[metric.key]}18`,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      </Stack>

      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          '& .MuiChartsLegend-root': {
            display: 'none',
          },
        }}
      >
        {chartType === 'pie' ? (
          <PieChart
            width={width}
            height={height}
            series={[
              {
                data: pieData,
                innerRadius: 0,
                outerRadius: 110,
                arcLabel: (item) =>
                  `${item.value}`,
                arcLabelMinAngle: 10,
              },
            ]}
            hideLegend
          />
        ) : chartType === 'bar' ? (
          <BarChart
            width={width}
            height={height}
            series={chartSeries.map((item) => ({
              id: item.id,
              label: item.label,
              data: item.data,
              color: item.color,
              barLabel: 'value',
            }))}
            xAxis={[
              {
                scaleType: 'band',
                data: labels,
              },
            ]}
            yAxis={[
              {
                width: 45,
              },
            ]}
            margin={{
              top: 40,
              right: 20,
              bottom: 45,
              left: 50,
            }}
            hideLegend
            barLabel={(item) => {
              const value = item?.value;

              if (
                value === undefined ||
                value === null ||
                Number(value) === 0
              ) {
                return '';
              }

              return String(value);
            }}
            barLabelPlacement="middle"
            sx={{
              '& .MuiBarLabel-root': {
                fill: '#ffffff',
                fontSize: '12px',
                fontWeight: 700,
                pointerEvents: 'none',
              },
            }}
          />
        ) : (
          <LineChart
            width={width}
            height={height}
            series={chartSeries}
            xAxis={[
              {
                scaleType: 'point',
                data: chartLabels,
              },
            ]}
            yAxis={[
              {
                width: 50,
              },
            ]}
            margin={{
              top: 45,
              right: 30,
              bottom: 50,
              left: 55,
            }}
            slots={{
              mark: ValueMark,
            }}
            hideLegend
            sx={{
              '& .MuiLineElement-root': {
                strokeWidth:
                  chartType === 'area'
                    ? 3
                    : 2,
              },
              '& .MuiAreaElement-root': {
                opacity:
                  chartType === 'area'
                    ? 0.3
                    : 0,
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
}